import React, { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/router";

import { FilterAsCheckbox } from "./FilterAsCheckbox";
import FilterSkeleton from "../loading-skeleton/FilterSkeleton";
import GroupedCheckboxList from "./GroupedCheckboxList";
import addFiltersToPageUrl from "./addFiltersToPageUrl";
import getFilterLabel from "./getFilterLabel";
import ShowMoreButton from "./ShowMoreButton";
import {
  showMoreOptions,
  filterDisclaimers,
  onlyShowOptions,
} from "./filtersConfig";
import { Box, Typography } from "@bca/ui-library";

interface Filter {
  filterKey: string;
  filterValues: { name: string; count: number }[];
}

const sortFiltersAlphabetically = (arrayToSort: Filter[]): Filter[] => {
  arrayToSort.sort((a, b) => {
    const item1 = a.filterKey;
    const item2 = b.filterKey;

    if (item1 < item2) {
      return -1;
    }
    if (item1 > item2) {
      return 1;
    }
    return 0;
  });

  return arrayToSort;
};

let timer;

const CheckboxGroup = ({
  filteredSubfilters,
  categoryKey,
  setSelectedVehicles,
  setRefreshCollapsible,
  setAppliedCheckboxes,
  parentFilterKey,
  appliedCheckboxes,
  additionalInformation,
}) => {
  const sortedFilteredSubfilters =
    (categoryKey === "MechanicalReportType" ||
      categoryKey === "mechanicalreporttype") &&
    filteredSubfilters
      ? sortFiltersAlphabetically(filteredSubfilters)
      : filteredSubfilters;

  const router = useRouter();

  const showDisclaimer = () => {
    additionalInformation?.disclaimer?.values?.forEach((filter) => {
      if (sortedFilteredSubfilters.find((item) => item.filterKey === filter))
        return true;
    });
    return false;
  };

  const [sliceSize, setSliceSize] = useState(
    Boolean(showMoreOptions[categoryKey])
      ? showMoreOptions[categoryKey].visibleOptionsCount
      : sortedFilteredSubfilters?.length
  );

  const stringifiedFilters = JSON.stringify(appliedCheckboxes);
  const debounceUrlUpdate = useCallback(() => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      const newUrl = addFiltersToPageUrl("bq", appliedCheckboxes);
      const currentPageUrl = new URL(window.location.href);
      if (newUrl.href === currentPageUrl.href) {
        return;
      }
      newUrl.searchParams.delete("page");
      newUrl.searchParams.delete("vrm");
      router.push(newUrl.href, newUrl.href, { shallow: true });
    }, 1000);
  }, [stringifiedFilters, appliedCheckboxes, router]);

  useEffect(() => {
    debounceUrlUpdate();
  }, [stringifiedFilters, debounceUrlUpdate]);

  const onChange = (filterKey, categoryKey, groupedBy, parentFilterKey) => {
    setSelectedVehicles([]);
    let newState = null;

    const filterAlreadyApplied = appliedCheckboxes.find((item) => {
      if (Boolean(item.groupedBy))
        return (
          item.filterKey === filterKey &&
          item.categoryKey.toLowerCase() === categoryKey.toLowerCase() &&
          item.groupedBy === groupedBy
        );
      return (
        item.filterKey === filterKey &&
        item.categoryKey.toLowerCase() === categoryKey.toLowerCase()
      );
    });
    const otherDependentFilters = appliedCheckboxes.filter(
      (item) =>
        item.parentFilterKey === categoryKey && item.groupedBy === filterKey
    );

    if (filterAlreadyApplied) {
      newState = [
        ...appliedCheckboxes.filter(
          (item) =>
            item !== filterAlreadyApplied &&
            !otherDependentFilters.find(
              (value) =>
                value.filterKey.toLowerCase() === item.filterKey.toLowerCase()
            )
        ),
      ];
    } else {
      newState = [
        ...appliedCheckboxes,
        { filterKey: filterKey, categoryKey, groupedBy, parentFilterKey },
      ];
    }

    setAppliedCheckboxes([...newState]);
  };

  return (
    <>
      {!sortedFilteredSubfilters && (
        <FilterSkeleton pairCount={categoryKey === "LogbookStatus" ? 1 : 3} />
      )}
      {sortedFilteredSubfilters
        ?.slice(0, sliceSize)
        .map(({ filterKey, filterValues }) => {
          if (
            filterValues?.length === 1 &&
            filterValues[0].name === filterKey
          ) {
            const disclaimerText = filterDisclaimers.filter(
              (item) =>
                item.filterKey === filterKey && item.categoryKey === categoryKey
            )[0]?.disclaimerText;
            if (
              !Boolean(onlyShowOptions[categoryKey]) ||
              onlyShowOptions[categoryKey].includes(filterKey)
            )
              return (
                <Box key={filterKey}>
                  <FilterAsCheckbox
                    key={filterKey}
                    id={filterKey}
                    label={getFilterLabel(categoryKey, filterKey)}
                    resultsCount={filterValues[0].count}
                    isSelected={Boolean(
                      appliedCheckboxes.find(
                        (item) =>
                          item.filterKey === filterKey &&
                          item.categoryKey.toLowerCase() ===
                            categoryKey.toLowerCase()
                      )
                    )}
                    onChange={() =>
                      onChange(filterKey, categoryKey, "", parentFilterKey)
                    }
                  />
                  {disclaimerText && (
                    <Typography
                      color="grey-blue"
                      variant="caption"
                      fontWeight="light"
                      ml={9}
                      css={`
                        max-width: 292px;
                      `}
                    >
                      {disclaimerText}
                    </Typography>
                  )}
                </Box>
              );

            return null;
          }

          return (
            <GroupedCheckboxList
              key={`${categoryKey}-${filterKey}`}
              filterKey={filterKey}
              categoryKey={categoryKey}
              filterValues={filterValues}
              onChange={onChange}
              selectedCheckboxes={appliedCheckboxes}
              setRefreshCollapsible={setRefreshCollapsible}
              parentFilterKey={parentFilterKey}
            />
          );
        })}
      <ShowMoreButton
        categoryKey={categoryKey}
        filteredSubfilters={sortedFilteredSubfilters}
        sliceSize={sliceSize}
        setSliceSize={setSliceSize}
        setRefreshCollapsible={setRefreshCollapsible}
      />
      {additionalInformation?.disclaimer?.key && showDisclaimer && (
        <Typography
          color="grey-blue"
          variant="caption"
          fontWeight="light"
          css={`
            max-width: 292px;
          `}
        >
          {additionalInformation?.disclaimer?.key}
        </Typography>
      )}
    </>
  );
};

export default CheckboxGroup;
